/**
 * "Answers for you" (proposal 1, kanban 059dd612). The owner: clicking an
 * answer opened a chat overlay, leaving it cleared every answer, and no
 * answer said which question or case it belonged to.
 *
 * One row per answer nobody has marked seen: who answered which question,
 * when it was asked and answered, how it came in, and where it belongs (a
 * case, else an item, else nowhere yet). The Triage list, the Responded
 * count, the tab badge and the drawer all read this one list.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import type { DoorInvestigationRead, DoorThreadRead } from '../../services/qcpInvestigations';
import { stripQuotedText } from '../../utils/emailQuoteStripping';
import { unseenAnswer } from './respondedView';
import { asksForThread, caseForAsk } from './qualityWorkspaceView';

export interface AnswerRow {
  request: DoorDataRequestRead;
  /** Who answered, as the record names them. */
  who: string;
  /** The question asked. */
  question: string;
  /** When the answer came in (ISO). */
  at: string;
  /** When the ask went out (ISO). */
  askedAt: string;
  /** The medium the answer came by ('email', 'slack', 'widget', …). */
  channel: string;
  /** The case it belongs to, if any. */
  investigation: DoorInvestigationRead | null;
  /** The item it belongs to when it is not on a case. */
  thread: DoorThreadRead | null;
  /** Path from the door's index to where it belongs; null for neither. */
  path: string | null;
}

function ms(iso: string | null | undefined): number {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(t) ? 0 : t;
}

/** The newest response on an ask (the answer the row speaks for). */
export function latestResponse(request: DoorDataRequestRead) {
  return [...(request.responses ?? [])].sort((a, b) => ms(b.created_at) - ms(a.created_at))[0] ?? null;
}

/** The item an ask belongs to: the thread it was born on, or the one whose
 *  chat it was filed from. */
function threadForAsk(request: DoorDataRequestRead, threads: DoorThreadRead[]): DoorThreadRead | null {
  return threads.find((t) => asksForThread([request], t).length > 0) ?? null;
}

/** One row for an ask, whether or not it is still unread (the drawer shows
 *  a seen answer the same way). Null for an ask with no answer. */
export function answerRow(
  request: DoorDataRequestRead,
  investigations: DoorInvestigationRead[],
  threads: DoorThreadRead[],
): AnswerRow | null {
  const last = latestResponse(request);
  const at = last?.created_at ?? request.answered_at;
  if (!at) return null;
  const unseen = unseenAnswer(request);
  const investigation = caseForAsk(request, investigations, threads);
  const thread = investigation ? null : threadForAsk(request, threads);
  return {
    request,
    who: unseen?.who ?? request.target_name,
    question: request.question_title,
    at,
    askedAt: request.sent_at ?? request.created_at,
    channel: last?.channel ?? '',
    investigation,
    thread,
    path: investigation
      ? `cases/${encodeURIComponent(investigation.investigation_no)}`
      : thread
        ? `threads/${encodeURIComponent(thread.thread_no)}`
        : null,
  };
}

/** Every answer not yet marked seen, newest first. */
export function answersForYou(
  requests: DoorDataRequestRead[],
  investigations: DoorInvestigationRead[],
  threads: DoorThreadRead[],
): AnswerRow[] {
  return requests
    .filter((r) => unseenAnswer(r) !== null)
    .map((r) => answerRow(r, investigations, threads))
    .filter((row): row is AnswerRow => row !== null)
    .sort((a, b) => ms(b.at) - ms(a.at));
}

/** The Responded count: unread answers, one per answer. */
export function respondedCount(rows: AnswerRow[]): number {
  return rows.length;
}

/** An emailed answer with the quoted history (our own ask, the headers)
 *  split off so the reply reads first and the rest can fold away. */
export function splitQuoted(body: string): { body: string; quoted: string[] } {
  const { body: kept, stripped } = stripQuotedText(body);
  if (!stripped) return { body: body.trim(), quoted: [] };
  // stripQuotedText keeps whole lines up to the quote marker (trailing blank
  // lines trimmed), so the quoted part is every line after the kept ones.
  const keptLines = kept.split('\n').length;
  const quoted = body.split(/\r?\n/).slice(keptLines).join('\n').trim();
  return { body: kept.trim(), quoted: quoted ? [quoted] : [] };
}

/** "by email" / "on Slack" / "in the portal" / "by text": how it came in. */
export function channelPhrase(channel: string): string {
  switch (channel) {
    case 'email':
      return 'by email';
    case 'slack':
      return 'on Slack';
    case 'widget':
      return 'in the portal';
    case 'sms':
      return 'by text';
    case 'manual':
      return 'entered by hand';
    case 'found':
      // The daily sweep found it elsewhere (kanban 3eed0be5): nobody replied.
      return 'found by Merlin';
    default:
      return channel ? `by ${channel}` : '';
  }
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

const clock = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const day = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/** "10:07 AM" today, "yesterday", else "Oct 8": a row's answered time. */
export function answeredWhen(at: string, now: Date = new Date()): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  if (sameDay(d, now)) return clock(d);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return 'yesterday';
  return day(d);
}

/** "today 10:07 AM" / "yesterday 2:15 PM" / "Oct 8 2:15 PM": the drawer's. */
export function answeredAtLong(at: string, now: Date = new Date()): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  const short = answeredWhen(at, now);
  if (sameDay(d, now)) return `today ${short}`;
  return `${short} ${clock(d)}`;
}

/** "Oct 8": when an ask went out. */
export function askedOn(at: string): string {
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? '' : day(d);
}

/** "asked Oct 8 by Merlin for Jess": door asks go out from Merlin's mailbox
 *  on the door user's behalf. */
export function askedLine(row: Pick<AnswerRow, 'askedAt'>, userName: string | null): string {
  const first = (userName ?? '').split(' ')[0];
  return `asked ${askedOn(row.askedAt)} by Merlin${first ? ` for ${first}` : ''}`;
}
